import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Button,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Card,
  CardContent,
  Grid,
  Chip,
  IconButton,
  Alert,
  Divider,
  Stack,
  List,
  ListItem,
  ListItemText,
  ListItemSecondaryAction,
  Paper,
  Accordion,
  AccordionSummary,
  AccordionDetails
} from '@mui/material';
import {
  Add as AddIcon,
  Delete as DeleteIcon,
  ExpandMore as ExpandMoreIcon,
  Save as SaveIcon,
  ArrowBack as ArrowBackIcon,
  Settings as SettingsIcon,
  TrendingUp as TrendingUpIcon,
  Stop as StopIcon,
  ExitToApp as ExitIcon
} from '@mui/icons-material';
import { useNavigate, useParams } from 'react-router-dom';
import { tradePlansApi } from '../../api/tradePlans';
import DWLFLoader from '../../components/DWLFLoader';

const TradePlanEditor = () => {
  const { planId } = useParams();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  const [planData, setPlanData] = useState({
    name: '',
    description: '',
    category: 'general',
    timeframe: '1d',
    entryCriteria: '',
    initialStopRules: {
      type: 'percentage',
      value: 3.0,
      description: '3% stop loss'
    },
    takeProfitRules: [
      {
        type: 'r_multiple',
        value: 2.0,
        description: '2R target'
      }
    ],
    managementRules: [
      'Move to breakeven at 1R'
    ],
    exitCriteria: [
      'Stop loss hit',
      'Take profit reached'
    ],
    tags: []
  });

  const categories = [
    { value: 'swing_trading', label: 'Swing Trading' },
    { value: 'day_trading', label: 'Day Trading' },
    { value: 'scalping', label: 'Scalping' },
    { value: 'position_trading', label: 'Position Trading' },
    { value: 'general', label: 'General' }
  ];

  const timeframes = [
    { value: '1m', label: '1 Minute' },
    { value: '5m', label: '5 Minutes' },
    { value: '15m', label: '15 Minutes' },
    { value: '1h', label: '1 Hour' },
    { value: '4h', label: '4 Hours' },
    { value: '1d', label: 'Daily' },
    { value: '1w', label: 'Weekly' }
  ];

  const stopTypes = [
    { value: 'percentage', label: 'Percentage' },
    { value: 'below_recent_low', label: 'Below Recent Low' },
    { value: 'atr', label: 'ATR Multiple' },
    { value: 'fixed', label: 'Fixed Price' }
  ];

  const takeProfitTypes = [
    { value: 'r_multiple', label: 'R Multiple' },
    { value: 'percentage', label: 'Percentage' },
    { value: 'resistance_level', label: 'Resistance Level' }
  ];

  useEffect(() => {
    if (planId && planId !== 'new') {
      loadPlan();
    }
  }, [planId]);

  const loadPlan = async () => {
    try {
      setLoading(true);
      const response = await tradePlansApi.getTradePlan(planId);
      const plan = response.data || response;
      setPlanData({
        name: plan.name || '',
        description: plan.description || '',
        category: plan.category || 'general',
        timeframe: plan.timeframe || '1d',
        entryCriteria: plan.entryCriteria || '',
        initialStopRules: plan.initialStopRules || {
          type: 'percentage',
          value: 3.0,
          description: '3% stop loss'
        },
        takeProfitRules: plan.takeProfitRules || [
          {
            type: 'r_multiple',
            value: 2.0,
            description: '2R target'
          }
        ],
        managementRules: plan.managementRules || [
          'Move to breakeven at 1R'
        ],
        exitCriteria: plan.exitCriteria || [
          'Stop loss hit',
          'Take profit reached'
        ],
        tags: plan.tags || []
      });
    } catch (err) {
      setError('Failed to load trade plan');
      console.error('Error loading trade plan:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    try {
      setSaving(true);
      if (planId && planId !== 'new') {
        await tradePlansApi.updateTradePlan(planId, planData);
        setSuccess('Trade plan updated successfully');
        // Navigate back to trade plans list after a short delay to show success message
        setTimeout(() => {
          navigate('/trade-plans');
        }, 1500);
      } else {
        const response = await tradePlansApi.createTradePlan(planData);
        setSuccess('Trade plan created successfully');
        // Navigate back to trade plans list after a short delay to show success message
        setTimeout(() => {
          navigate('/trade-plans');
        }, 1500);
      }
    } catch (err) {
      setError('Failed to save trade plan');
      console.error('Error saving trade plan:', err);
    } finally {
      setSaving(false);
    }
  };

  const addTakeProfitRule = () => {
    setPlanData({
      ...planData,
      takeProfitRules: [
        ...planData.takeProfitRules,
        {
          type: 'r_multiple',
          value: 2.0,
          description: 'New take profit rule'
        }
      ]
    });
  };

  const removeTakeProfitRule = (index) => {
    setPlanData({
      ...planData,
      takeProfitRules: planData.takeProfitRules.filter((_, i) => i !== index)
    });
  };

  const updateTakeProfitRule = (index, field, value) => {
    const updatedRules = [...planData.takeProfitRules];
    updatedRules[index] = { ...updatedRules[index], [field]: value };
    setPlanData({ ...planData, takeProfitRules: updatedRules });
  };

  const addManagementRule = () => {
    setPlanData({
      ...planData,
      managementRules: [...planData.managementRules, 'New management rule']
    });
  };

  const removeManagementRule = (index) => {
    setPlanData({
      ...planData,
      managementRules: planData.managementRules.filter((_, i) => i !== index)
    });
  };

  const updateManagementRule = (index, value) => {
    const updatedRules = [...planData.managementRules];
    updatedRules[index] = value;
    setPlanData({ ...planData, managementRules: updatedRules });
  };

  const addExitCriteria = () => {
    setPlanData({
      ...planData,
      exitCriteria: [...planData.exitCriteria, 'New exit criteria']
    });
  };

  const removeExitCriteria = (index) => {
    setPlanData({
      ...planData,
      exitCriteria: planData.exitCriteria.filter((_, i) => i !== index)
    });
  };

  const updateExitCriteria = (index, value) => {
    const updatedCriteria = [...planData.exitCriteria];
    updatedCriteria[index] = value;
    setPlanData({ ...planData, exitCriteria: updatedCriteria });
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <DWLFLoader size="medium" />
      </Box>
    );
  }

  return (
    <Box sx={{ p: 3 }}>
      <Box display="flex" alignItems="center" mb={3}>
        <IconButton onClick={() => navigate('/trade-plans')} sx={{ mr: 2 }}>
          <ArrowBackIcon />
        </IconButton>
        <Typography variant="h4" component="h1">
          {planId === 'new' ? 'Create Trade Plan' : 'Edit Trade Plan'}
        </Typography>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {success && (
        <Alert severity="success" sx={{ mb: 3 }} onClose={() => setSuccess(null)}>
          {success}
        </Alert>
      )}

      <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1fr 360px' }, gap: 3 }}>
        {/* Basic Information */}
        <Box>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Basic Information
              </Typography>
              <Box
                sx={{
                  display: 'grid',
                  gap: 2,
                  gridTemplateColumns: { xs: '1fr', md: 'repeat(4, 1fr)' }
                }}
              >
                <Box sx={{ gridColumn: { xs: 'span 1', md: 'span 4' } }}>
                  <TextField
                    fullWidth
                    label="Plan Name"
                    value={planData.name}
                    onChange={(e) => setPlanData({ ...planData, name: e.target.value })}
                    required
                  />
                </Box>
                <Box sx={{ gridColumn: { xs: 'span 1', md: 'span 2' } }}>
                  <FormControl fullWidth>
                    <InputLabel>Category</InputLabel>
                    <Select
                      value={planData.category}
                      onChange={(e) => setPlanData({ ...planData, category: e.target.value })}
                      label="Category"
                    >
                      {categories.map((category) => (
                        <MenuItem key={category.value} value={category.value}>
                          {category.label}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Box>
                <Box sx={{ gridColumn: { xs: 'span 1', md: 'span 2' } }}>
                  <FormControl fullWidth>
                    <InputLabel>Timeframe</InputLabel>
                    <Select
                      value={planData.timeframe}
                      onChange={(e) => setPlanData({ ...planData, timeframe: e.target.value })}
                      label="Timeframe"
                    >
                      {timeframes.map((timeframe) => (
                        <MenuItem key={timeframe.value} value={timeframe.value}>
                          {timeframe.label}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Box>
                <Box sx={{ gridColumn: { xs: 'span 1', md: 'span 4' } }}>
                  <TextField
                    fullWidth
                    label="Description"
                    value={planData.description}
                    onChange={(e) => setPlanData({ ...planData, description: e.target.value })}
                    multiline
                    rows={3}
                  />
                </Box>
              </Box>
            </CardContent>
          </Card>

          {/* Entry Criteria */}
          <Card sx={{ mt: 3 }}>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Entry Criteria
              </Typography>
              <TextField
                fullWidth
                label="Entry Criteria"
                value={planData.entryCriteria}
                onChange={(e) => setPlanData({ ...planData, entryCriteria: e.target.value })}
                multiline
                rows={4}
                placeholder="Describe your entry criteria in detail..."
              />
            </CardContent>
          </Card>

          {/* Stop Loss Rules */}
          <Card sx={{ mt: 3 }}>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Initial Stop Loss Rules
              </Typography>
              <Box
                sx={{
                  display: 'grid',
                  gap: 2,
                  gridTemplateColumns: { xs: '1fr', md: '240px 160px 1fr' }
                }}
              >
                <FormControl fullWidth>
                  <InputLabel>Stop Type</InputLabel>
                  <Select
                    value={planData.initialStopRules.type}
                    onChange={(e) => setPlanData({
                      ...planData,
                      initialStopRules: {
                        ...planData.initialStopRules,
                        type: e.target.value
                      }
                    })}
                    label="Stop Type"
                  >
                    {stopTypes.map((type) => (
                      <MenuItem key={type.value} value={type.value}>
                        {type.label}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
                <TextField
                  fullWidth
                  label="Value"
                  type="number"
                  value={planData.initialStopRules.value}
                  onChange={(e) => setPlanData({
                    ...planData,
                    initialStopRules: {
                      ...planData.initialStopRules,
                      value: parseFloat(e.target.value)
                    }
                  })}
                />
                <TextField
                  fullWidth
                  label="Description"
                  value={planData.initialStopRules.description}
                  onChange={(e) => setPlanData({
                    ...planData,
                    initialStopRules: {
                      ...planData.initialStopRules,
                      description: e.target.value
                    }
                  })}
                />
              </Box>
            </CardContent>
          </Card>

          {/* Take Profit Rules */}
          <Card sx={{ mt: 3 }}>
            <CardContent>
              <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
                <Typography variant="h6">
                  Take Profit Rules
                </Typography>
                <Button
                  startIcon={<AddIcon />}
                  onClick={addTakeProfitRule}
                  size="small"
                >
                  Add Rule
                </Button>
              </Box>
              {planData.takeProfitRules.map((rule, index) => (
                <Paper key={index} sx={{ p: 2, mb: 2 }}>
                  <Box
                    sx={{
                      display: 'grid',
                      gap: 2,
                      gridTemplateColumns: { xs: '1fr', md: '240px 160px 1fr auto' },
                      alignItems: 'center'
                    }}
                  >
                    <FormControl fullWidth>
                      <InputLabel>Type</InputLabel>
                      <Select
                        value={rule.type}
                        onChange={(e) => updateTakeProfitRule(index, 'type', e.target.value)}
                        label="Type"
                      >
                        {takeProfitTypes.map((type) => (
                          <MenuItem key={type.value} value={type.value}>
                            {type.label}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                    <TextField
                      fullWidth
                      label="Value"
                      type="number"
                      value={rule.value}
                      onChange={(e) => updateTakeProfitRule(index, 'value', parseFloat(e.target.value))}
                    />
                    <TextField
                      fullWidth
                      label="Description"
                      value={rule.description}
                      onChange={(e) => updateTakeProfitRule(index, 'description', e.target.value)}
                    />
                    <IconButton
                      size="small"
                      onClick={() => removeTakeProfitRule(index)}
                      color="error"
                    >
                      <DeleteIcon />
                    </IconButton>
                  </Box>
                </Paper>
              ))}
            </CardContent>
          </Card>

          {/* Management Rules */}
          <Card sx={{ mt: 3 }}>
            <CardContent>
              <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
                <Typography variant="h6">
                  Position Management Rules
                </Typography>
                <Button
                  startIcon={<AddIcon />}
                  onClick={addManagementRule}
                  size="small"
                >
                  Add Rule
                </Button>
              </Box>
              <List>
                {planData.managementRules.map((rule, index) => (
                  <ListItem key={index}>
                    <ListItemText>
                      <TextField
                        fullWidth
                        value={rule}
                        onChange={(e) => updateManagementRule(index, e.target.value)}
                        placeholder="Enter management rule..."
                      />
                    </ListItemText>
                    <ListItemSecondaryAction>
                      <IconButton
                        edge="end"
                        onClick={() => removeManagementRule(index)}
                        color="error"
                      >
                        <DeleteIcon />
                      </IconButton>
                    </ListItemSecondaryAction>
                  </ListItem>
                ))}
              </List>
            </CardContent>
          </Card>

          {/* Exit Criteria */}
          <Card sx={{ mt: 3 }}>
            <CardContent>
              <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
                <Typography variant="h6">
                  Exit Criteria
                </Typography>
                <Button
                  startIcon={<AddIcon />}
                  onClick={addExitCriteria}
                  size="small"
                >
                  Add Criteria
                </Button>
              </Box>
              <List>
                {planData.exitCriteria.map((criteria, index) => (
                  <ListItem key={index}>
                    <ListItemText>
                      <TextField
                        fullWidth
                        value={criteria}
                        onChange={(e) => updateExitCriteria(index, e.target.value)}
                        placeholder="Enter exit criteria..."
                      />
                    </ListItemText>
                    <ListItemSecondaryAction>
                      <IconButton
                        edge="end"
                        onClick={() => removeExitCriteria(index)}
                        color="error"
                      >
                        <DeleteIcon />
                      </IconButton>
                    </ListItemSecondaryAction>
                  </ListItem>
                ))}
              </List>
            </CardContent>
          </Card>
        </Box>

        {/* Preview Panel */}
        <Box>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Plan Preview
              </Typography>
              <Stack spacing={2}>
                <Box>
                  <Typography variant="subtitle2" color="text.secondary">
                    Name
                  </Typography>
                  <Typography variant="body1">
                    {planData.name || 'Untitled Plan'}
                  </Typography>
                </Box>
                <Box>
                  <Typography variant="subtitle2" color="text.secondary">
                    Category
                  </Typography>
                  <Chip
                    label={categories.find(c => c.value === planData.category)?.label || planData.category}
                    size="small"
                  />
                </Box>
                <Box>
                  <Typography variant="subtitle2" color="text.secondary">
                    Timeframe
                  </Typography>
                  <Typography variant="body1">
                    {timeframes.find(t => t.value === planData.timeframe)?.label || planData.timeframe}
                  </Typography>
                </Box>
                <Box>
                  <Typography variant="subtitle2" color="text.secondary">
                    Stop Loss
                  </Typography>
                  <Typography variant="body1">
                    {planData.initialStopRules.description}
                  </Typography>
                </Box>
                <Box>
                  <Typography variant="subtitle2" color="text.secondary">
                    Take Profit Rules
                  </Typography>
                  <Typography variant="body2">
                    {planData.takeProfitRules.length} rule(s)
                  </Typography>
                </Box>
                <Box>
                  <Typography variant="subtitle2" color="text.secondary">
                    Management Rules
                  </Typography>
                  <Typography variant="body2">
                    {planData.managementRules.length} rule(s)
                  </Typography>
                </Box>
              </Stack>
            </CardContent>
          </Card>

          {/* Actions */}
          <Card sx={{ mt: 3 }}>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Actions
              </Typography>
              <Stack spacing={2}>
                <Button
                  fullWidth
                  variant="contained"
                  startIcon={<SaveIcon />}
                  onClick={handleSave}
                  disabled={saving || !planData.name}
                >
                  {saving ? 'Saving...' : 'Save Plan'}
                </Button>
                <Button
                  fullWidth
                  variant="outlined"
                  onClick={() => navigate('/trade-plans')}
                >
                  Cancel
                </Button>
              </Stack>
            </CardContent>
          </Card>
        </Box>
      </Box>
    </Box>
  );
};

export default TradePlanEditor;
